(() => {
  if (window.LlovableMedia) return;
  const hydrated = new WeakSet(), failedWebm = new WeakSet();
  const selectedMobile = new WeakMap();
  const mobile = window.matchMedia('(max-width:749px)');
  const presentation = video => {
    if (mobile.matches) {
      if (video.hasAttribute('poster')) video.dataset.llnDesktopPoster = video.getAttribute('poster');
      video.removeAttribute('poster');
    } else if (video.dataset.llnDesktopPoster) video.setAttribute('poster', video.dataset.llnDesktopPoster);
  };
  const sources = video => [...video.querySelectorAll('source')];
  const hydrate = (video, preload = 'auto') => {
    presentation(video);
    video.preload = preload;
    const mobileUrl = mobile.matches && !video.dataset.llnFullQuality
      ? sources(video).find(source => source.dataset.mobileSrc)?.dataset.mobileSrc : null;
    if (hydrated.has(video) && (selectedMobile.get(video) || null) === mobileUrl) return false;
    if (failedWebm.has(video)) return false;
    let changed = false;
    if (mobileUrl) {
      video.src = mobileUrl;
      selectedMobile.set(video, mobileUrl);
      hydrated.add(video);
      video.load();
      return true;
    }
    if (selectedMobile.has(video)) {
      video.removeAttribute('src');
      selectedMobile.delete(video);
      changed = true;
    }
    sources(video).forEach(source => {
      if (source.dataset.src && !source.getAttribute('src')) {
        source.src = source.dataset.src;
        changed = true;
      }
    });
    if (video.dataset.src && !video.getAttribute('src')) {
      video.src = video.dataset.src;
      changed = true;
    }
    hydrated.add(video);
    if (changed) video.load();
    return changed;
  };
  const fallback = video => {
    if (failedWebm.has(video)) return false;
    const list = sources(video);
    const hasWebm = list.some(source => source.type === 'video/webm');
    const mp4 = list.find(source => source.type === 'video/mp4');
    const url = mp4?.dataset.src || mp4?.getAttribute('src');
    if (!hasWebm || !url) return false;
    failedWebm.add(video);
    video.dataset.llnMediaFallback = 'true';
    const resumeAt = video.currentTime;
    if (Number.isFinite(resumeAt) && resumeAt > 0) video.addEventListener('loadedmetadata', () => {
      try { video.currentTime = resumeAt; } catch (_) {}
    }, {once:true});
    video.src = url;
    video.load();
    return true;
  };
  const copySources = (from, to) => {
    to.dataset.llnFullQuality = 'true';
    if (from.dataset.llnMediaFallback === 'true') {
      to.dataset.src = from.currentSrc || from.getAttribute('src');
      return;
    }
    const list = sources(from);
    list.forEach(source => {
      const copy = source.cloneNode();
      copy.dataset.src = source.dataset.src || source.getAttribute('src');
      copy.removeAttribute('src');
      copy.removeAttribute('data-mobile-src');
      delete copy.dataset.mobileSrc;
      to.append(copy);
    });
    if (!list.length) to.dataset.src = from.dataset.src || from.currentSrc || from.getAttribute('src') || '';
  };
  const release = video => {
    video.pause();
    video.removeAttribute('src');
    sources(video).forEach(source => source.removeAttribute('src'));
    hydrated.delete(video);
    selectedMobile.delete(video);
    failedWebm.delete(video);
    delete video.dataset.llnMediaFallback;
    video.load();
  };
  window.LlovableMedia = {hydrate, fallback, copySources, release, presentation};
  const init = scope => {
    scope.querySelectorAll('video[data-lln-lazy-autoplay],video[data-lln-lazy-controls]').forEach(video => {
      if (video.dataset.llnLazyReady) return;
      video.dataset.llnLazyReady = 'true';
      const hiddenSlide = () => video.hasAttribute('data-lln-gallery-autoplay') && video.closest('[data-media-id]')?.hidden;
      const autoplay = () => video.hasAttribute('data-lln-lazy-autoplay') || mobile.matches;
      let near = false, userPlaying = false, pending = false, blocked = false, playToken = 0;
      video.addEventListener('play', () => { if (!autoplay()) userPlaying = true; });
      video.addEventListener('pause', () => { if (!video.error) userPlaying = false; });
      const play = () => {
        if (!near || hiddenSlide() || document.hidden || !autoplay() || pending || blocked || !video.paused) return;
        video.muted = true;
        video.defaultMuted = true;
        hydrate(video);
        pending = true;
        const token = ++playToken;
        video.play().then(() => {
          if (!near || hiddenSlide() || document.hidden) video.pause();
        }).catch(error => { if (token === playToken && error.name === 'NotAllowedError') blocked = true; }).finally(() => { if (token === playToken) pending = false; });
      };
      const sync = () => {
        presentation(video);
        if (mobile.matches) hydrate(video, near ? 'auto' : 'metadata');
        if (near && !document.hidden) {
          hydrate(video, autoplay() ? 'auto' : 'metadata');
          play();
        } else if (autoplay()) { ++playToken; pending = false; video.pause(); video.preload = 'metadata'; }
      };
      video.addEventListener('error', () => {
        if (fallback(video)) {
          if (autoplay()) play();
          else if (userPlaying) video.play().catch(() => {});
        }
      });
      video.addEventListener('canplay', play);
      const retry = () => { blocked = false; sync(); };
      window.addEventListener('pointerup', retry);
      window.addEventListener('pageshow', retry);
      document.addEventListener('visibilitychange', sync);
      const modeChanged = () => { ++playToken; pending = false; blocked = false; video.pause(); sync(); };
      mobile.addEventListener('change', modeChanged);
      if (mobile.matches) sync();
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => {
          near = entries[0].isIntersecting;
          sync();
        }, {rootMargin:mobile.matches ? '80px 0px' : '400px 0px', threshold:0});
        observer.observe(video);
        const unload = event => {
          if (!event.target.contains(video)) return;
          observer.disconnect();
          video.pause();
          window.removeEventListener('pointerup', retry);
          window.removeEventListener('pageshow', retry);
          document.removeEventListener('visibilitychange', sync);
          mobile.removeEventListener('change', modeChanged);
          document.removeEventListener('shopify:section:unload', unload);
        };
        document.addEventListener('shopify:section:unload', unload);
      } else {
        near = true;
        sync();
      }
    });
  };
  window.LlovableMedia.init = init;
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
})();
